window.customElements.define(
  'custom-variant-picker',
  class CustomVariantPicker extends window.HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.handleSubscriptionSubmit = this.handleSubscriptionSubmit.bind(this);
      this.handleBuyOnceSubmit = this.handleBuyOnceSubmit.bind(this);
      this.handleStickyAddToCartChange = this.handleStickyAddToCartChange.bind(this);
    }

    connectedCallback() {
      this.initDOMElements();
      this.initEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('form');
      this.buyOnceForm = this.querySelector('#buy-once-form');
      this.currentOptionName = this.querySelector('#current-option-name');
      this.addToCartSubscriptionPrice = this.querySelector('#add-to-cart-subscription-price');
      this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
      this.buyOncePrice = this.querySelector('#buy-once-price');
      this.errorMessage = this.querySelector('#error-message');
      this.addToCartButton = this.querySelector('#add-to-cart');
      this.notifyMeButton = this.querySelector('#notify-me-button');
    }

    // Event Listeners
    initEventListeners() {
      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('submit', this.handleSubscriptionSubmit);
      this.buyOnceForm.addEventListener('submit', this.handleBuyOnceSubmit);

      // Listen for sticky add to cart changes
      window.addEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange);
    }

    removeEventListeners() {
      this.form.removeEventListener('change', this.handleChange);
      this.form.removeEventListener('submit', this.handleSubscriptionSubmit);
      this.buyOnceForm.removeEventListener('submit', this.handleBuyOnceSubmit);

      // Remove sticky add to cart listener
      window.removeEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange);
    }

    // Handlers
    handleStickyAddToCartChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantInput = this.form.querySelector(`input[type="radio"][value="${variant.variantId}"]`);

      if (!variantInput) return;
      variantInput.checked = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-variant-picker:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              sellingPlanId: currentVariantData.sellingPlanId,
              price: currentVariantData.price,
              compareAtPrice: currentVariantData.crossedPrice,
              subscriptionPrice: currentVariantData.subscriptionPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleSubscriptionSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.form.classList.add('is-loading');
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
          selling_plan: currentVariantData.sellingPlanId,
        });

        this.dispatchCartRefresh();
        this.closeModalIfNeeded();
      } catch (error) {
        console.error('Failed to add subscription to cart:', error);
        this.errorMessage.textContent = error.message;
        this.errorMessage.classList.remove('hidden');

        setTimeout(() => {
          this.errorMessage.classList.add('hidden');
        }, 3000);
      } finally {
        this.form.classList.remove('is-loading');
        this.isSubmitting = false;
      }
    }

    async handleBuyOnceSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.buyOnceForm.classList.add('is-loading');
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
        });

        this.dispatchCartRefresh();
        this.closeModalIfNeeded();
      } catch (error) {
        console.error('Failed to add item to cart:', error);
        this.errorMessage.textContent = error.message;
        this.errorMessage.classList.remove('hidden');

        setTimeout(() => {
          this.errorMessage.classList.add('hidden');
        }, 3000);
      } finally {
        this.buyOnceForm.classList.remove('is-loading');
        this.isSubmitting = false;
      }
    }

    handleUpdateUI(currentVariantData) {
      if (this.currentOptionName) {
        this.currentOptionName.textContent = currentVariantData.deliveryInterval;
      }

      if (this.addToCartSubscriptionPrice) {
        this.addToCartSubscriptionPrice.textContent = currentVariantData.subscriptionPrice;
      }

      if (this.addToCartCompareAtPrice) {
        this.addToCartCompareAtPrice.textContent = currentVariantData.crossedPrice;
      }

      if (this.buyOncePrice) {
        this.buyOncePrice.textContent = currentVariantData.price;
      }

      if (this.addToCartButton) {
        this.addToCartButton.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.buyOnceForm) {
        this.buyOnceForm.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.notifyMeButton) {
        this.notifyMeButton.classList.toggle('hidden', currentVariantData.available);
      }
    }

    handleUpdateUrl(currentVariantData) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // Utility functions
    async addToCart(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();

      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    closeModalIfNeeded() {
      if (this.form.dataset.closeModalOnAdd === 'true') {
        const quickBuyDrawer = this.closest('quick-buy-drawer');
        if (quickBuyDrawer && typeof quickBuyDrawer.hide === 'function') {
          quickBuyDrawer.hide();
        }
      }
    }

    getCurrentVariantData() {
      const currentVariant = this.form.querySelector('input[type="radio"]:checked');
      const { variantId, sellingPlanId, deliveryInterval, crossedPrice, subscriptionPrice, savings, available, price } =
        currentVariant.dataset;

      return {
        variantId,
        sellingPlanId,
        deliveryInterval,
        crossedPrice,
        subscriptionPrice,
        savings,
        available,
        price,
      };
    }
  }
);
